/**
 * Describing a video and what optimizing changed about it, as plain strings.
 * Pure (no DOM, type-only library imports) so it can be unit-tested on its own
 * (test/vitest/tools-app/).
 */
import type { VideoProbeReport } from 'trace-viewer/media-utils';
import { formatFileSize } from '../shared/ui';

export function formatBitrate(bps: number | null): string {
  if (bps == null) return 'unknown';
  if (bps >= 1_000_000) return `${(bps / 1_000_000).toFixed(2)} Mbps`;
  return `${Math.round(bps / 1000)} kbps`;
}

export function formatDuration(sec: number): string {
  if (sec < 60) return `${sec.toFixed(1)} s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** The properties optimizing can change, each as a comparable display string. */
export function changeFields(r: VideoProbeReport): Record<string, string> {
  const v = r.video;
  const a = r.audio;
  return {
    Resolution: v ? `${v.width} × ${v.height}` : 'None',
    'Video codec': v ? `${v.codec}${v.profile ? ` ${v.profile}` : ''}` : 'None',
    Bitrate: formatBitrate(v?.bitrateBps ?? null),
    'Frame rate': v?.framerate != null ? `${Math.round(v.framerate)} fps${v.framerateMode === 'vfr' ? ' (variable)' : ''}` : 'Unknown',
    Audio: a ? `${a.codec}, ${formatBitrate(a.bitrateBps)}` : 'None',
    'Starts playing quickly': r.container.faststart ? 'Yes' : 'No',
    Length: r.durationSec > 0 ? formatDuration(r.durationSec) : 'Unknown',
    'File size': formatFileSize(r.container.fileSize),
  };
}

/**
 * One sentence naming what optimizing actually did, for the size meter —
 * so the headline number comes with its reason. File size is left out (the
 * meter already shows it) and so is length (never the point of optimizing).
 */
export function summarizeChanges(before: VideoProbeReport, after: VideoProbeReport): string {
  const b = changeFields(before);
  const a = changeFields(after);
  const parts: string[] = [];
  if (b.Resolution !== a.Resolution) parts.push(`resized to ${a.Resolution}`);
  if (b['Video codec'] !== a['Video codec']) parts.push(`re-encoded as ${a['Video codec']}`);
  if (b.Bitrate !== a.Bitrate) parts.push(`bitrate ${b.Bitrate} → ${a.Bitrate}`);
  if (b['Frame rate'] !== a['Frame rate']) parts.push(`frame rate ${b['Frame rate']} → ${a['Frame rate']}`);
  if (b.Audio !== a.Audio) parts.push(a.Audio === 'None' ? 'audio removed' : `audio ${a.Audio}`);
  if (b['Starts playing quickly'] === 'No' && a['Starts playing quickly'] === 'Yes') parts.push('now starts playing quickly');
  if (parts.length === 0) return '';
  const text = parts.join(', ');
  return `${text[0].toUpperCase()}${text.slice(1)}.`;
}
